import React, { useEffect, useState } from 'react';
import ChartComponent from '../../../components/chart';
import Api from '../../../services/api';
import FilterCohost2 from '../../../components/filterCohost2';
import { Spin } from 'antd';
import moment from 'moment';
import {useParams} from "react-router-dom";

const MonthlyCohostChart = ({ chartTitle, chartKey, data, loading }) => {
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });
    const [showPercentage, setShowPercentage] = useState(false);
    const [totalsByMonth, setTotalsByMonth] = useState([]);

    const disablePercentageToggle = chartKey === 'monthly_mau';

    useEffect(() => {
        const stackedBarColors = [
            '#5FD3BC', '#FFCC80', '#F48FB1', '#4FC3F7', '#42A5F5',
            '#64B5F6', '#D1C4E9', '#FFD54F', '#388E3C', '#1976D2',
            '#E53935', '#66BB6A', '#BA68C8', '#FFB74D'
        ];

        if (data && data.labels && data.datasets && data.datasets[chartKey]) {
            const selectedGroup = data.datasets[chartKey];
            const numberOfMonths = data.labels.length;
            const totals = Array(numberOfMonths).fill(0);

            const calculateTotal = !disablePercentageToggle;
            if (calculateTotal) {
                Object.values(selectedGroup).forEach(values => {
                    values.forEach((value, i) => {
                        totals[i] += value || 0; // tránh NaN khi null
                    });
                });
                setTotalsByMonth(totals);
            }

            // 🔹 Tạo map đổi tên M0, M1... thành tháng mới nhất → cũ nhất
            const mLabelMap = {};
            data.labels.slice().reverse().forEach((month, index) => {
                mLabelMap[`M${index}`] = month;
            });

            const transformedDatasets = Object.keys(selectedGroup).map((mKey, index) => {
                const values = selectedGroup[mKey];
                const finalData = (!disablePercentageToggle && showPercentage)
                    ? values.map((val, i) => totals[i] ? (val / totals[i]) * 100 : 0)
                    : values;

                // Nếu là chart monthly_mau thì đổi nhãn M0, M1... thành tháng tương ứng
                let labelName = mKey;
                if (chartKey === 'monthly_mau') {
                    const mIndex = parseInt(mKey.replace('M', ''), 10);
                    // Đảo ngược: M0 là tháng cuối, M1 là tháng trước đó...
                    const reversedLabels = [...data.labels].reverse();
                    labelName = reversedLabels[mIndex] || mKey;
                }

                return {
                    label: labelName,
                    data: finalData,
                    rawData: values,
                    backgroundColor: stackedBarColors[index % stackedBarColors.length],
                    stack: 'stack1'
                };
            });


            setChartData({
                labels: data.labels,
                datasets: transformedDatasets
            });
        }
    }, [data, chartKey, showPercentage]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'right' },
            title: {
                display: true,
                text: chartTitle,
                align: 'start',
                font: { size: 16 }
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        const datasetIndex = tooltipItem.datasetIndex;
                        const dataset = chartData.datasets[datasetIndex];
                        const monthIndex = tooltipItem.dataIndex;
                        const rawValue = dataset.rawData[monthIndex];

                        // Bỏ qua các M có value = 0 hoặc null
                        if (!rawValue) return null;

                        const total = totalsByMonth[monthIndex];
                        const percentage = total ? (rawValue / total) * 100 : 0;

                        // Nếu là monthly_mau thì đổi tên M thành tháng
                        if (chartKey === 'monthly_mau') {
                            const reversedLabels = [...chartData.labels].reverse();
                            const mappedMonth = reversedLabels[datasetIndex] || '';

                            if (disablePercentageToggle) {
                                return `${mappedMonth}: ${rawValue.toLocaleString()}`;
                            }

                            return showPercentage
                                ? `${mappedMonth}: ${percentage.toFixed(2)}% (${rawValue.toLocaleString()})`
                                : `${mappedMonth}: ${rawValue.toLocaleString()} (${percentage.toFixed(2)}%)`;
                        }

                        // Các chart khác giữ nguyên label
                        if (disablePercentageToggle) {
                            return `${dataset.label}: $${rawValue.toLocaleString()}`;
                        }

                        return showPercentage
                            ? `${dataset.label}: ${percentage.toFixed(2)}% (${rawValue.toLocaleString()})`
                            : `${dataset.label}: ${rawValue.toLocaleString()} (${percentage.toFixed(2)}%)`;
                    },
                    footer: (tooltipItems) => {
                        const monthIndex = tooltipItems[0]?.dataIndex;
                        const total = totalsByMonth[monthIndex];
                        return disablePercentageToggle ? null : `Total: ${total.toLocaleString()}`;
                    }
                }
            }

        },
        interaction: { mode: 'index', intersect: false },
        scales: {
            y: {
                stacked: true,
                beginAtZero: true,
                title: {
                    display: false,
                    text: showPercentage && !disablePercentageToggle ? 'Tỷ lệ (%)' : 'Giá trị ($)'
                },
                ticks: {
                    callback: (value) =>
                        showPercentage && !disablePercentageToggle
                            ? `${value}%`
                            : `${value}`
                }
            },
            x: {
                stacked: true
            }
        }
    };


    return (
        <div className="mb-5">
            <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>{chartTitle}</h3>

                {!disablePercentageToggle && (
                    <div style={{ marginTop: 7 }}>
                        <span style={{ marginRight: 10 }}>Compare percentage</span>
                        <label className="toggle-percentage-switch">
                            <input
                                type="checkbox"
                                checked={showPercentage}
                                onChange={() => setShowPercentage(!showPercentage)}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                )}
            </div>

            <div style={{ height: '400px' }}>
                <Spin spinning={loading}>
                    <ChartComponent type="bar" data={chartData} options={chartOptions} />
                </Spin>
            </div>
        </div>


    );
};

const MonthlyCohostCharts = () => {
    const [monthRange, setMonthRange] = useState({
        startDate: moment().subtract(5, 'month'),
        endDate: moment(),
    });

    const [filters, setFilters] = useState({
        from_month: moment().subtract(5, "month").format("YYYY-MM"),
        to_month: moment().format("YYYY-MM"),
    });

    const { code, os } = useParams();

    const [selectedOptions, setSelectedOptions] = useState([]);
    const [selectedOptionName, setSelectedOptionName] = useState(['Ad Revenue', 'Ad ROAS']);
    const [metricFilters, setMetricFilters] = useState('ad_revenue');
    const [loading, setLoading] = useState(false);
    const [hasRunReport, setHasRunReport] = useState(false);

    const metric_array = [
        { key: "ad_revenue", name: "Ad Revenue" },
        { key: "iap_revenue", name: "IAP Revenue" },
        { key: "sub_revenue", name: "Subscription Revenue" },
        { key: "total_revenue", name: "Total Revenue" },
    ];

    const getMetricName = (key) => {
        const found = metric_array.find(opt => opt.key === key);
        return found ? found.name : key;
    };

    const [fullData, setFullData] = useState({
        labels: [],
        datasets: {}
    });

    const handleRunReport = async ({ startDate, endDate, selectedOptions, metricFilters, selectedName }) => {
        setLoading(true);
        setSelectedOptionName(selectedName);
        setHasRunReport(true);

        try {
            const res = await Api.get(`/api/app/monthly_cohort`, {
                params: {
                    code: code,
                    os: os,
                    from: monthRange.startDate.format('YYYY-MM-01'),
                    to: monthRange.endDate.format('YYYY-MM-01'),
                    options: selectedOptions.join(','),
                    metric: metricFilters,
                }
            });

            setSelectedOptions(selectedOptions || []);
            setMetricFilters(metricFilters || null);

            const dataChart = res.data.data_chart || {};

            const allLabelsRaw = Object.values(dataChart)[0]?.labels || [];
            const formattedLabels = allLabelsRaw.map(dateStr => moment(dateStr, 'MM/YYYY').format('MMM YYYY'));

            const datasets = {};
            for (const [chartKey, chartData] of Object.entries(dataChart)) {
                datasets[chartKey] = chartData.datasets || {};
            }

            setFullData({
                labels: formattedLabels,
                datasets: datasets,
            });


        } catch (error) {
            console.error('Lỗi khi load dữ liệu:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMonthRangeChange = (range) => {
        setMonthRange(range);
        setFilters({
            ...filters,
            from_month: range.startDate.format("YYYY-MM"),
            to_month: range.endDate.format("YYYY-MM"),
        });
    };

    return (
        <div>
            <div className="desktop-filter">
                <FilterCohost2
                    monthRange={monthRange}
                    onMonthRangeChange={handleMonthRangeChange}
                    id="filter-tab1"
                    onRunReport={handleRunReport}
                />
            </div>
            {hasRunReport && (
                <>
                    <div className="mt-8">
                        <div className="chart-1">
                            <MonthlyCohostChart
                                loading={loading}
                                chartTitle={`${getMetricName(metricFilters)} by monthly cohorts`}
                                chartKey="revenue"
                                data={fullData}
                            />
                        </div>
                    </div>

                    <div className="mt-8">
                        <div className="chart-2">
                            <MonthlyCohostChart
                                loading={loading}
                                chartTitle="MAU by monthly cohorts"
                                chartKey="mau"
                                data={fullData}
                            />
                        </div>
                    </div>

                    <div className="mt-8">
                        <div className="chart-3">
                            <MonthlyCohostChart
                                loading={loading}
                                chartTitle=""
                                chartKey="monthly_mau"
                                data={fullData}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default MonthlyCohostCharts;
